import { useUpdateUserStatusMutation, User, IHttpError } from '@/@data'
import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { useNotification, useTranslator } from '@/hooks'
import { CircularProgress, Dialog, DialogActions, DialogContent, Typography } from '@mui/material'
import { Trans } from 'react-i18next'
import { Button } from '@/@core/components/mui/Button'
import { useNavigate } from 'react-router-dom'

import { useForm, FormProvider } from 'react-hook-form'

interface IDeleteUserModal {
  open: boolean
  isPending?: boolean
  setOpen: (open: boolean) => void
  dataDelete: any
  onSuccess: () => void
}

export function DeleteUserDialog({ open, setOpen, dataDelete, onSuccess }: IDeleteUserModal) {
  const { __ } = useTranslator()
  const n = useNotification()
  const navigate = useNavigate()

  const handleClose = () => {
    setOpen(false)
  }

  const methods = useForm({})

  const { isPending } = useUpdateUserStatusMutation()

  const { useDeleteMutation } = User.objects.getCommonMutations()
  const deleteUserMutation = useDeleteMutation()

  const handleDeleteUser = () => {
    deleteUserMutation.mutate(dataDelete.id, {
      onSuccess: () => {
        n.success(__("You've successfully delete user"))
        onSuccess()
        navigate('/cms/users/employee')
      },
      onError: (error: IHttpError) => {
        n.error(error.message)
      }
    })
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      scroll='body'
      fullWidth
      closeAfterTransition={false}
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', maxWidth: '800px' }
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleDeleteUser)}>
          <DialogCloseButton onClick={handleClose} sx={{ position: 'absolute', right: 0, top: 0, zIndex: 1302 }}>
            <i className='tabler-x' />
          </DialogCloseButton>

          <DialogContent className='text-center'>
            <Typography variant='h5' gutterBottom sx={{ fontWeight: 'medium', fontSize: '28px', mb: 0 }}>
              {__('Confirm Account Deletion')}
            </Typography>

            <Typography variant='body1' gutterBottom sx={{ mb: 2 }}>
              <Trans
                i18nKey='Are you sure you want to delete the account associated with phone number <strong>{{phone}}</strong>? Once confirmed, this account will be permanently removed from the system and cannot be recovered.'
                values={{ phone: dataDelete?.phone }}
                components={{ strong: <strong /> }}
              />
            </Typography>
          </DialogContent>

          <DialogActions className='px-[20px] sm:px-[24px] text-center justify-center gap-2'>
            <Button
              type='submit'
              variant='contained'
              disabled={isPending}
              sx={{ borderRadius: 10, height: 38, backgroundColor: '#3551F2', color: '#fff' }}
            >
              {isPending ? (
                <>
                  <CircularProgress size={16} color='inherit' />
                  {__('Updating...')}
                </>
              ) : (
                __('Delete')
              )}
            </Button>
            <Button
              variant='contained'
              onClick={handleClose}
              sx={{ borderRadius: 10, backgroundColor: '#6F83F629', height: 38, color: '#6F83F6' }}
            >
              {__('Cancel')}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
